<template>
  <v-container>
    <v-layout row>
      <v-flex xs12 sm8 offset-sm2>
        <div class="piece-detail">
          <router-link to="/my-pieces" class="back-link">&larr; Back to My Corps Pieces</router-link>

          <div v-if="loading && !piece" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else-if="!piece" class="py-8">
            <p>Corps piece not found.</p>
          </div>

          <div v-else>
            <h1>{{ piece.title }}</h1>
            <p v-if="piece.description" class="piece-description">{{ piece.description }}</p>

            <!-- Stage Formation -->
            <div v-if="piece.stageFormationUrl" class="section">
              <h2>Stage Formation</h2>
              <a :href="piece.stageFormationUrl" target="_blank" class="formation-link">View Stage Formation →</a>
            </div>

            <!-- My Roles -->
            <div v-for="role in myRoles" :key="role.id" class="section">
              <h2>{{ role.name }}</h2>

              <!-- Costume Details -->
              <h3>Costume Details</h3>
              <p v-if="role.costumeDescription" class="body-text">{{ role.costumeDescription }}</p>
              <p v-if="role.costumeColorField && dancer[role.costumeColorField]" class="body-text">
                Your assigned color: <strong>{{ dancer[role.costumeColorField] }}</strong>
              </p>
              <ul v-if="role.costumeItems && role.costumeItems.length > 0" class="costume-list">
                <li v-for="item in role.costumeItems" :key="item.id">
                  {{ item.description }}
                  <a
                    v-if="item.purchaseLink"
                    :href="item.purchaseLink"
                    target="_blank"
                    class="purchase-link"
                  >Purchase →</a>
                </li>
              </ul>

              <!-- Rehearsal Videos -->
              <h3>Rehearsal Videos</h3>
              <div v-if="resolveVideos(role).length > 0">
                <div
                  v-for="video in resolveVideos(role)"
                  :key="video.id"
                  class="video-section"
                >
                  <h4>{{ video.title }}</h4>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div class="video-embed" v-html="video.embedCode"></div>
                </div>
              </div>
              <p v-else class="body-text muted">Videos coming soon.</p>
            </div>

            <div v-if="myRoles.length === 0" class="py-4">
              <p class="muted">You don't appear to be assigned to any roles in this piece.</p>
            </div>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style scoped>
.piece-detail {
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  display: inline-block;
  margin-bottom: 12px;
  transition: color 0.15s;
}
.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin-bottom: 12px;
}
h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 26px;
  letter-spacing: -.01em;
  margin-bottom: 8px;
  margin-top: 36px;
  border-bottom: 1px solid rgba(0,0,0,.1);
  padding-bottom: 6px;
}
h3 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  margin: 20px 0 8px;
}
h4 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 17px;
  margin: 16px 0 6px;
}
.piece-description {
  font-family: var(--iabf-font-body, inherit);
  font-size: 18px;
  color: rgba(40, 26, 23, .7);
  line-height: 1.7em;
  margin-bottom: 24px;
}
.body-text {
  font-family: var(--iabf-font-body, inherit);
  font-size: 18px;
  color: rgba(40, 26, 23, .7);
  line-height: 1.7em;
  margin-bottom: 8px;
}
.muted {
  color: rgba(40, 26, 23, .45);
}
.section {
  margin-bottom: 32px;
}
.formation-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 18px;
  color: var(--iabf-primary, #8a957d);
  text-decoration: none;
}
.formation-link:hover {
  text-decoration: underline;
}
.costume-list {
  font-family: var(--iabf-font-body, inherit);
  font-size: 18px;
  color: rgba(40, 26, 23, .7);
  line-height: 1.7em;
  padding-left: 20px;
  margin-bottom: 8px;
}
.purchase-link {
  color: var(--iabf-primary, #8a957d);
  text-decoration: none;
  margin-left: 8px;
}
.purchase-link:hover {
  text-decoration: underline;
}
.video-section {
  margin-bottom: 24px;
}
.video-embed {
  width: 100%;
}
</style>

<script>
export default {
  props: ['pieceId'],
  computed: {
    loading () {
      return this.$store.getters.loading
    },
    piece () {
      return this.$store.getters.currentIABFCorpsPiece
    },
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    dancer () {
      return this.currentIABFDancer || {}
    },
    dancerId () {
      return this.dancer.id || null
    },
    myRoles () {
      if (!this.piece || !this.dancerId) return []
      return (this.piece.roles || []).filter(r =>
        r.dancers && r.dancers.some(d => d.dancerId === this.dancerId)
      )
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.$store.dispatch('loadIABFCorpsPiece', this.pieceId)
  },
  methods: {
    resolveVideos (role) {
      if (role.shareVideosFromRole && this.piece) {
        const sourceRole = (this.piece.roles || []).find(r => r.id === role.shareVideosFromRole)
        if (sourceRole) {
          return (sourceRole.videos || []).slice().sort((a, b) => a.order - b.order)
        }
      }
      return (role.videos || []).slice().sort((a, b) => a.order - b.order)
    }
  }
}
</script>
